import React, { useEffect, useRef, useState } from "react";
import { useSelector } from 'react-redux';
import { ToastContainer } from "react-toastify";
import {
  getTeamLeads,
  editTeamLead,
  deleteTeamLead,
  addTeamLead,
} from "../../services/api/api";
import Loader from "../loader/Loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { Divider } from "primereact/divider";
import {
  fetchData,
  showToast,
  formateName,
  trimObjectValues,
  getSerialNo,
} from "../utils/Function/Helper";
import {
  NewButton,
  DeleteButton,
  ActionButton,
  SearchBar,
  NormalButton,
} from "../utils/Atoms/Atoms";

const TeamLeadList = () => {
  const user = useSelector((state) => state.user);    
  let emptyData = {
    superadmin_id : user?.userDetails?.ID || null,
    id: null,
    teamlead_name: "",
    teamlead_email: "",
    teamlead_password: "",
    role: "Admin",
  };

  const [data, setData] = useState(emptyData);
  const [datas, setDatas] = useState([]);
  const [errors, setErrors] = useState({});
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedDatas, setSelectedDatas] = useState(null);
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingapi, setLoadingapi] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const dt = useRef(null);

  // loader
  const loader = () => {
    setBuffering(true);
    setTimeout(() => {
      setBuffering(false);
    }, 500); // 0.5 second delay
  };

  // get datas
  useEffect(() => {
    setDatas([]);
    if (emptyData.superadmin_id) {
      fetchData(() => getTeamLeads(emptyData), setDatas);
    } 
    loader();
  }, [user, loadingapi]);

  // open add popup
  const openDialog = () => {
    setData(emptyData);
    setErrors({});
    setPopup(1);
  };

  // close popup
  const hideDialog = () => {
    setData(emptyData);
    setPopup(null);
  };

  // update inputs
  const onInputChange = (e, name) => {
    let _data = { ...data };
    let val = (e.target && e.target.value).trimStart() || "";
    switch (name) {
      case "teamlead_name": {
        val = formateName(val);
        break;
      }

      case "teamlead_email": {
        val = val.toLowerCase();
        break;
      }

      default: {
        val = val;
        break;
      }
    }
    _data[name] = val;
    if (val) setErrors({ ...errors, [name]: "" }); // Clear error on input change
    setData(_data);
  };

  // reseting the page
  const resetPage = () => {
    hideDialog();
    setLoadingapi((prev) => !prev);
  };

  // handle Api Response
  const handleApiResponse = (response) => {
    if (response?.status) {
      showToast("success", response.message);
      resetPage();
    } else {
      showToast("warning", response?.message || response, "top-center");
    }
  };

  // validate all fields
  const validateField = (finalData) => {
    const newErrors = {};

    // name validation
    if (!finalData.teamlead_name) {
      newErrors.teamlead_name = "Name is required";
    } else {
      const isNameExist = datas.some(
        (item) =>
          item.id !== finalData.id &&
          item.teamlead_name.toLowerCase() === finalData.teamlead_name.toLowerCase()
      );
      if (isNameExist) {
        showToast("warning", `Name Already Exist !`, "top-center");
        newErrors.teamlead_name = "Name already exist";
      }
    }

    // email validation
    if (!finalData.teamlead_email) {
      newErrors.teamlead_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(finalData.teamlead_email)) {
      newErrors.teamlead_email = "Please enter a valid email";
    } else {
      const isEmailExist = datas.some(
        (item) =>
          item.id !== finalData.id &&
          item.teamlead_email.toLowerCase() === finalData.teamlead_email.toLowerCase()
      );
      if (isEmailExist) {
        showToast("warning", `Email Already Exist !`, "top-center");
        newErrors.teamlead_email = "Email already exist";
      }
    }

    // password validation
    if (!finalData.teamlead_password) {
      newErrors.teamlead_password = "Password is required";
    } else if (finalData.teamlead_password.length < 6) {
      newErrors.teamlead_password = "Password must be at least 6 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  // add and edit Item
  const addItem = async () => {
    let _data = trimObjectValues(data);

    // validation
    if (validateField(_data)) {
      // Perform API call
      setLoading(true);
      const response = _data.id ? await editTeamLead(_data) : await addTeamLead(_data);
      handleApiResponse(response);
      setLoading(false);
    }
  };

  // save and cancel button
  const dataDialogFooter = (
    <React.Fragment>
      <NormalButton
        label="Cancel"
        icon="pi-times"
        outlined={true}
        onClick={hideDialog}
      />
      <NormalButton
        label="Save"
        icon="pi-check"
        disabled={loading}
        onClick={addItem}
      />
    </React.Fragment>
  );

  // add and delete button
  const rightToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <SearchBar
          position={"left"}
          onInput={(e) => setGlobalFilter(e.target.value)}
        />
        <NewButton onClick={openDialog} />
        <DeleteButton
          onClick={() => setPopup(3)}
          disabled={!selectedDatas || !selectedDatas.length}
        />
      </div>
    );
  };

  // export button
  const leftToolbarTemplate = () => {
    return <h3 className="m-0 hcolor">Team Leads List</h3>;
  };

  // action buttons
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex">
        <ActionButton
          icon={"pi-pen-to-square"}
          onClick={() => {
            setData({ ...rowData });
            setErrors({});
            setPopup(1);
          }}
          id={"editTooltip"}
          toolText={"Edit"}
        />
        <ActionButton
          icon={"pi-trash"}
          onClick={() => {
            setData({ ...rowData });
            setPopup(2);
          }}
          id={"deleteTooltip"}
          toolText={"Delete"}
        />
      </div>
    );
  };

  // delete one
  const deleteData = async () => {
    setLoading(true);    
    const response = await deleteTeamLead(data);
    if (response?.status) {
      showToast("success", response.message);
      resetPage();
    } else {
      showToast("warning", response?.message || response);
    }
    setLoading(false);
  };

  // delete many
  const deleteSelectedDatas = async () => {
    setLoading(true);
    try {
      for (const item of selectedDatas) {
        const response = await deleteTeamLead(item);
        if (!response?.status) {
          showToast("warning", response?.message || response);
          return;
        }
      }
      showToast("success", `Team Leads Deleted`);
      setSelectedDatas(null);
      resetPage();
    } catch (error) {
      showToast("error", "An error occurred while deleting data.");
    } finally {
      setLoading(false);
    }
  };

  // delete footer buttons
  const deleteDialogFooter = (callTo) => {
    return (
      <React.Fragment>
        <NormalButton
          label="No"
          icon="pi-times"
          outlined={true}
          onClick={hideDialog}
        />
        <NormalButton
          label="Yes"
          icon="pi-check"
          disabled={loading}
          onClick={callTo}
        />
      </React.Fragment>
    );
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <ToastContainer />
      {buffering ? (
        <Loader />
      ) : (
        <div className="card">
          <Toolbar
            className="mb-4"
            start={leftToolbarTemplate}
            end={rightToolbarTemplate}
          ></Toolbar>

          {/* table */}
          <DataTable
            ref={dt}
            value={datas}
            paginator
            rows={10}
            className="datatable"
            rowsPerPageOptions={[5, 10, 15]}
            globalFilter={globalFilter}
            selection={selectedDatas}
            onSelectionChange={(e) => setSelectedDatas(e.value)}
            selectionMode="checkbox"
            emptyMessage={`No Team Leads Available`}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} datas"
          >
            <Column
              selectionMode="multiple"
              exportable={false}
              style={{ width: "30px" }}
            />
            <Column
              header="S.No"
              body={getSerialNo}
              style={{ width: "30px" }}
            />
            <Column field="teamlead_name" sortable header="Name" />
            <Column field="teamlead_email" sortable header="Email" />
            <Column field="teamlead_password" header="Password" />
            <Column
              header="Action"
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "50px" }}
            />
          </DataTable>
        </div>
      )}

      {/* 1. add and edit data dialog */}
      <Sidebar
        visible={popup === 1}
        position="right"
        header={<h4 className="mt-3 mb-0">TeamLead Details</h4>}
        onHide={hideDialog}
        style={{ width: window.innerWidth < 768 ? "20rem" : "25rem" }}
        aria-label="Sidebar Details"
        closeOnEscape={false}
        dismissable={false}
      >
        <Divider />

        <div className="field mb-3">
          <div>
            <label htmlFor="username" className="font-bold mb-1">
              Name *
            </label>
          </div>
          <InputText
            id="teamlead_name"
            value={data.teamlead_name}
            onChange={(e) => onInputChange(e, "teamlead_name")}
            placeholder="Please Enter Name"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.teamlead_name,
            })}
          />
          <div>
            {errors.teamlead_name && (
              <small className="p-error">{errors.teamlead_name}</small>
            )}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="email" className="font-bold mb-1">
              Email *
            </label>
          </div>
          <InputText
            id="teamlead_email"
            value={data.teamlead_email}
            onChange={(e) => onInputChange(e, "teamlead_email")}
            placeholder="Please Enter Email"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.teamlead_email,
            })}
          />
          <div>
            {errors.teamlead_email && <small className="p-error">{errors.teamlead_email}</small>}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="password" className="font-bold mb-1">
              Password *
            </label>
          </div>
          <InputText
            id="teamlead_password"
            value={data.teamlead_password}
            onChange={(e) => onInputChange(e, "teamlead_password")}
            placeholder="Please Enter Password"
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.teamlead_password,
            })}
          />
          <div>
            {errors.teamlead_password && (
              <small className="p-error">{errors.teamlead_password}</small>
            )}
          </div>
        </div>

        <div className="field mt-5">{dataDialogFooter}</div>
      </Sidebar>

      {/* 2. delete single data dialog */}
      <Dialog
        visible={popup === 2}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteData)}
        onHide={hideDialog}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>
              Are you sure you want to delete <b>{data.teamlead_name}</b> ?
            </span>
          )}
        </div>
      </Dialog>

      {/* 3. delete many datas dialog */}
      <Dialog
        visible={popup === 3}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteSelectedDatas)}
        onHide={hideDialog}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>Are you sure you want to delete the selected Datas ?</span>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default TeamLeadList;
